import { Typography, Stack, GlobalStyles } from '@mui/material';

import {
  LearningHubRouteParams,
  routeForLearningHub,
} from '@watershed/shared-universal/dashboardRoutes';
import BoxLink from './BoxLink';
import Image from 'next/image';
import { ReactNode } from 'react';
import ArrowNEIcon from '@watershed/icons/components/ArrowNE';
import PopOutIcon from '@watershed/icons/components/PopOut';
import isExternalUrl from '../utils/isExternalUrl';

export type WellExternalLinkProps = {
  src?: string;
  title: ReactNode;
  description: ReactNode;
  href: string;
  gradient?: 'green' | 'blue';
};

const VAR_OFFSET_START = '--WellExternalLink-offset-start';
const VAR_OFFSET_END = '--WellExternalLink-offset-end';
const VAR_OFFSET_SM_END = '--WellExternalLink-offset-small-end';

/**
 * A link meant to be placed in a Well that links to content outside
 * the main app (e.g. our blog).
 */
export default function WellExternalLink({
  src,
  title,
  description,
  href,
  gradient = 'blue',
}: WellExternalLinkProps) {
  const Icon = isExternalUrl(href) ? PopOutIcon : ArrowNEIcon;
  return (
    <BoxLink
      href={href}
      target="_blank"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        p: 2,
        gap: 2,
        textDecoration: 'none',

        // If multiple are stacked, we want to round the corners of the first and last but
        // have a regular divider between the others
        '&:first-of-type': {
          borderTopLeftRadius: (theme) => theme.shape.borderRadius,
          borderTopRightRadius: (theme) => theme.shape.borderRadius,
        },
        '&:last-of-type': {
          borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
          borderBottomRightRadius: (theme) => theme.shape.borderRadius,
        },
        '&:not(:first-of-type)': {
          borderTop: 0,
        },

        border: (theme) => `1px solid ${theme.palette.divider}`,

        [VAR_OFFSET_END]: `calc(var(${VAR_OFFSET_START}) + 16%)`,
        [VAR_OFFSET_SM_END]: `calc(var(${VAR_OFFSET_START}) + 8%)`,
        transition: (theme) =>
          theme.transitions.create(VAR_OFFSET_START, {
            duration: theme.transitions.duration.standard,
          }),
        background:
          gradient === 'green'
            ? `linear-gradient(96deg, rgba(0, 187, 129, 0.08) var(${VAR_OFFSET_START}), rgba(255, 255, 255, 1) var(${VAR_OFFSET_END})) padding-box,
               linear-gradient(96deg, rgba(255, 255, 255, 1)  var(${VAR_OFFSET_START}), rgba(255, 255, 255, 1) var(${VAR_OFFSET_END})) padding-box,
               linear-gradient(96deg, rgba(0, 187, 129, 0.32) var(${VAR_OFFSET_START}), #EBEFF7 var(${VAR_OFFSET_SM_END})) border-box`
            : `linear-gradient(100deg, rgba(38, 92, 255, 0.08) var(${VAR_OFFSET_START}), rgba(255, 255, 255, 0.08) var(${VAR_OFFSET_END})) padding-box,
               linear-gradient(96deg,  rgba(255, 255, 255, 1)  var(${VAR_OFFSET_START}), rgba(255, 255, 255, 1) var(${VAR_OFFSET_END})) padding-box,
               linear-gradient(100deg, rgba(38, 92, 255, 0.32) var(${VAR_OFFSET_START}), #EBEFF7 var(${VAR_OFFSET_SM_END})) border-box`,
        boxShadow: '0px 1px 0.5px 0px rgba(79, 89, 110, 0.08)',
        '&:hover': {
          [VAR_OFFSET_START]: '16%',
        },
      }}
    >
      {/* NOTE(@lachlanjc): Ideally this wouldn’t be global but Emotion doesn’t seem to support @property other ways */}
      <GlobalStyles
        styles={`
          @property ${VAR_OFFSET_START} {
            syntax: '<percentage>';
            inherits: true;
            initial-value: 0%;
          }
        `}
      />
      {src && <Image src={src} width={80} height={80} alt="" />}
      <Stack gap={0.25} flexGrow={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" color={(theme) => theme.palette.cobalt70}>
            {title}
          </Typography>
          <Icon size={16} color={(theme) => theme.palette.cobalt70} />
        </Stack>
        <Typography variant="body2" sx={{ textWrap: 'balance' }}>
          {description}
        </Typography>
      </Stack>
    </BoxLink>
  );
}

/**
 * Variant of WellExternalLink making it easy to link to learning hub content.
 */
export function WellLearningHubLink({
  learningHubRouteParams,
  ...props
}: Omit<WellExternalLinkProps, 'href'> & {
  learningHubRouteParams: LearningHubRouteParams;
}) {
  return (
    <WellExternalLink
      {...props}
      href={routeForLearningHub(learningHubRouteParams)}
    />
  );
}
